import { ADDITIONAL_SIGNATORIES_FIELD_NAME } from '@/modules/templates/lib/signatory-fields';
import { resolveFieldText } from '@/i18n/utils/resolve-field-text';
import { useTranslation } from 'react-i18next';
import type { FieldRendererProps } from './types';

// Import new composite field renderers
import { AddressField } from './AddressField';
import { CurrencyField } from './CurrencyField';
import { PartyField } from './PartyField';
import { PercentageField } from './PercentageField';
import { PhoneField } from './PhoneField';
import { TextareaField } from './TextareaField';
import { UrlField } from './UrlField';
import { TextField } from './form-fields/TextField';
import { EmailField } from './form-fields/EmailField';
import { DateField } from './form-fields/DateField';
import { AdditionalSignatoriesField } from './form-fields/AdditionalSignatoriesField';
import { NumberField } from './form-fields/NumberField';
import { CheckboxField } from './form-fields/CheckboxField';
import { SelectField } from './form-fields/SelectField';
import { MultiSelectField } from './form-fields/MultiSelectField';

// Re-export types and utilities
export { COUNTRIES, COUNTRY_CODES, CURRENCIES } from './types';
export type {
  AddressValue,
  CurrencyValue,
  FieldConfig,
  FieldRendererProps,
  PartyValue,
  PhoneValue,
} from './types';
export * from './utils';

/**
 * Dynamic Field Renderer
 * Renders the appropriate field component based on field type.
 *
 * Resolves the field's label / helpText / placeholder / options text
 * against UILM here at the dispatch level so every leaf renderer stays
 * locale-blind — they keep reading `field.label` etc. but the values
 * are now whatever UILM serves (or the original text when no key is
 * published yet).
 */
export function DynamicField(props: FieldRendererProps) {
  const { t } = useTranslation();
  const localizedField = resolveFieldText(t, props.field);
  const localizedProps = { ...props, field: localizedField };
  const { field } = localizedProps;

  if (field.name === ADDITIONAL_SIGNATORIES_FIELD_NAME) {
    return <AdditionalSignatoriesField {...localizedProps} />;
  }

  switch (field.type) {
    case 'text':
      return <TextField {...localizedProps} />;
    case 'email':
      return <EmailField {...localizedProps} />;
    case 'date':
      return <DateField {...localizedProps} />;
    case 'number':
      return <NumberField {...localizedProps} />;
    case 'checkbox':
      return <CheckboxField {...localizedProps} />;
    case 'select':
      return <SelectField {...localizedProps} />;
    case 'multiselect':
      return <MultiSelectField {...localizedProps} />;
    // New composite field types
    case 'textarea':
      return <TextareaField {...localizedProps} />;
    case 'phone':
      return <PhoneField {...localizedProps} />;
    case 'address':
      return <AddressField {...localizedProps} />;
    case 'party':
      return <PartyField {...localizedProps} />;
    case 'currency':
      return <CurrencyField {...localizedProps} />;
    case 'percentage':
      return <PercentageField {...localizedProps} />;
    case 'url':
      return <UrlField {...localizedProps} />;
    default:
      return <TextField {...localizedProps} />;
  }
}

// Export individual field components for direct use
export {
  AddressField,
  CurrencyField,
  PartyField,
  PercentageField,
  PhoneField,
  TextareaField,
  UrlField,
  TextField,
  EmailField,
  DateField,
  AdditionalSignatoriesField,
  NumberField,
  CheckboxField,
  SelectField,
  MultiSelectField,
};

export default DynamicField;
